import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CalendarIcon,
  ChartBarIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { timeAttendanceAPI, employeeAPI, ukSchedulingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TimeAttendance = () => {
  const [activeTab, setActiveTab] = useState('clock');
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [employeeTimesheets, setEmployeeTimesheets] = useState([]);
  const [overtimeRecords, setOvertimeRecords] = useState([]);
  const [complianceReport, setComplianceReport] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Date range for reports
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  // Clock in/out form
  const [clockForm, setClockForm] = useState({
    notes: '',
    location: 'office'
  });

  // Overtime request form
  const [overtimeForm, setOvertimeForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    planned_hours: '',
    reason: '',
    notes: ''
  });

  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);

  const { user, isAdmin, isManager, isHRManager } = useAuth();

  // Tab configuration
  const tabs = [
    {
      id: 'clock',
      name: 'Clock In/Out',
      icon: ClockIcon,
      description: 'Track your working hours',
      roles: ['all']
    },
    {
      id: 'timesheets',
      name: 'My Timesheets',
      icon: DocumentTextIcon,
      description: 'View and submit timesheets',
      roles: ['all']
    },
    {
      id: 'approval',
      name: 'Timesheet Approval',
      icon: CheckCircleIcon,
      description: 'Approve employee timesheets',
      roles: ['manager', 'admin', 'hr_manager']
    },
    {
      id: 'overtime',
      name: 'Overtime Management',
      icon: ExclamationTriangleIcon,
      description: 'Manage overtime requests',
      roles: ['manager', 'admin', 'hr_manager']
    },
    {
      id: 'compliance',
      name: 'Compliance Report',
      icon: ChartBarIcon,
      description: 'Working hours compliance',
      roles: ['admin', 'hr_manager']
    }
  ];

  useEffect(() => {
    if (activeTab === 'clock') {
      fetchAttendanceStatus();
    } else if (activeTab === 'timesheets') {
      fetchEmployeeTimesheets();
    } else if (activeTab === 'approval') {
      fetchTimesheets();
      fetchEmployees();
    } else if (activeTab === 'overtime') {
      fetchOvertimeRecords();
      fetchEmployees();
    } else if (activeTab === 'compliance') {
      fetchComplianceReport();
    }
  }, [activeTab, dateRange]);

  const fetchAttendanceStatus = async () => {
    try {
      setLoading(true);
      const response = await timeAttendanceAPI.getAttendanceStatus(user.employee_id);
      setAttendanceStatus(response.data);
    } catch (error) {
      console.error('Error fetching attendance status:', error);
      setError('Failed to load attendance status');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const response = await timeAttendanceAPI.getTimesheets({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        status: 'pending_approval'
      });
      setTimesheets(response.data.timesheets || []);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
      setError('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeTimesheets = async () => {
    try {
      setLoading(true);
      const response = await timeAttendanceAPI.getTimesheets({
        employee_id: user.employee_id,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      setEmployeeTimesheets(response.data.timesheets || []);
    } catch (error) {
      console.error('Error fetching employee timesheets:', error);
      setError('Failed to load your timesheets');
    } finally {
      setLoading(false);
    }
  };

  const fetchOvertimeRecords = async () => {
    try {
      setLoading(true);
      const response = await timeAttendanceAPI.getOvertimeRecords({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      setOvertimeRecords(response.data.overtime_records || []);
    } catch (error) {
      console.error('Error fetching overtime records:', error);
      setError('Failed to load overtime records');
    } finally {
      setLoading(false);
    }
  };

  const fetchComplianceReport = async () => {
    try {
      setLoading(true);
      const response = await timeAttendanceAPI.getComplianceReport({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      setComplianceReport(response.data);
    } catch (error) {
      console.error('Error fetching compliance report:', error);
      setError('Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll({});
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      setLoading(true);
      await timeAttendanceAPI.clockIn({
        employee_id: user.employee_id,
        clock_in_time: new Date().toISOString(),
        notes: clockForm.notes,
        location: clockForm.location
      });
      
      setClockForm({ notes: '', location: 'office' });
      fetchAttendanceStatus();
    } catch (error) {
      console.error('Error clocking in:', error);
      setError('Failed to clock in');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setLoading(true);
      await timeAttendanceAPI.clockOut({
        employee_id: user.employee_id,
        clock_out_time: new Date().toISOString(),
        notes: clockForm.notes
      });
      
      setClockForm({ notes: '', location: 'office' });
      fetchAttendanceStatus();
    } catch (error) {
      console.error('Error clocking out:', error);
      setError('Failed to clock out');
    } finally {
      setLoading(false);
    }
  };

  const handleStartBreak = async () => {
    try {
      setLoading(true);
      await timeAttendanceAPI.startBreak({
        employee_id: user.employee_id,
        break_start_time: new Date().toISOString(),
        break_type: 'lunch'
      });
      
      fetchAttendanceStatus();
    } catch (error) {
      console.error('Error starting break:', error);
      setError('Failed to start break');
    } finally {
      setLoading(false);
    }
  };

  const handleEndBreak = async () => {
    try {
      setLoading(true);
      await timeAttendanceAPI.endBreak({
        employee_id: user.employee_id,
        break_end_time: new Date().toISOString()
      });
      
      fetchAttendanceStatus();
    } catch (error) {
      console.error('Error ending break:', error);
      setError('Failed to end break');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTimesheet = async (timesheetId, approved) => {
    try {
      if (approved) {
        await timeAttendanceAPI.approveTimesheet(timesheetId, {
          approved_by: user.id,
          approval_notes: 'Approved'
        });
      } else {
        const reason = prompt('Please provide a reason for rejection:');
        if (!reason) return;
        
        await timeAttendanceAPI.rejectTimesheet(timesheetId, {
          rejected_by: user.id,
          rejection_reason: reason
        });
      }
      
      fetchTimesheets();
    } catch (error) {
      console.error('Error updating timesheet:', error);
      setError('Failed to update timesheet');
    }
  };

  const handleSubmitTimesheet = async (timesheetId) => {
    try {
      await timeAttendanceAPI.submitTimesheet(timesheetId, {
        submitted_by: user.employee_id
      });
      
      fetchEmployeeTimesheets();
    } catch (error) {
      console.error('Error submitting timesheet:', error);
      setError('Failed to submit timesheet');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(timeString).toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.draft;
  };

  const renderClockTab = () => {
    return (
      <div className="space-y-6">
        {/* Current Status Card */}
        <div className="card">
          <div className="text-center">
            <div className="mb-6">
              <ClockIcon className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-900">
                {new Date().toLocaleTimeString('en-GB', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </h3>
              <p className="text-gray-600">{new Date().toLocaleDateString('en-GB', { 
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}</p>
            </div>

            {attendanceStatus && (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-600 mb-1">Clock In</div>
                    <div className="text-lg font-semibold text-blue-900">
                      {attendanceStatus.clock_in_time ? formatTime(attendanceStatus.clock_in_time) : '--:--'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-600 mb-1">Hours Worked</div>
                    <div className="text-lg font-semibold text-green-900">
                      {attendanceStatus.hours_worked ? formatDuration(attendanceStatus.hours_worked) : '0h 0m'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <div className="text-sm text-yellow-600 mb-1">Status</div>
                    <div className="text-lg font-semibold text-yellow-900">
                      {attendanceStatus.is_clocked_in ? 
                        (attendanceStatus.is_on_break ? 'On Break' : 'Working') : 
                        'Not Clocked In'
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center space-x-4">
              {!attendanceStatus?.is_clocked_in ? (
                <button
                  onClick={handleClockIn}
                  disabled={loading}
                  className="btn-primary flex items-center"
                >
                  <PlayIcon className="w-5 h-5 mr-2" />
                  Clock In
                </button>
              ) : (
                <>
                  {!attendanceStatus.is_on_break ? (
                    <>
                      <button
                        onClick={handleStartBreak}
                        disabled={loading}
                        className="btn-secondary flex items-center"
                      >
                        <PauseIcon className="w-5 h-5 mr-2" />
                        Start Break
                      </button>
                      <button
                        onClick={handleClockOut}
                        disabled={loading}
                        className="btn-primary bg-red-600 hover:bg-red-700 flex items-center"
                      >
                        <StopIcon className="w-5 h-5 mr-2" />
                        Clock Out
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleEndBreak}
                      disabled={loading}
                      className="btn-primary flex items-center"
                    >
                      <PlayIcon className="w-5 h-5 mr-2" />
                      End Break
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Clock Notes */}
        <div className="card">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Notes (Optional)</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <select
                className="form-input"
                value={clockForm.location}
                onChange={(e) => setClockForm({ ...clockForm, location: e.target.value })}
              >
                <option value="office">Office</option>
                <option value="home">Home/Remote</option>
                <option value="client_site">Client Site</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                className="form-input"
                rows="3"
                value={clockForm.notes}
                onChange={(e) => setClockForm({ ...clockForm, notes: e.target.value })}
                placeholder="Optional notes about your work location or tasks..."
              />
            </div>
          </div>
        </div>

        {/* Compliance Warnings */}
        {attendanceStatus?.compliance_warnings && attendanceStatus.compliance_warnings.length > 0 && (
          <div className="card border-l-4 border-yellow-400 bg-yellow-50">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mt-1 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800 mb-2">Working Time Compliance Warnings</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {attendanceStatus.compliance_warnings.map((warning, index) => (
                    <li key={index}>• {warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTimesheetsTab = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">My Timesheets</h3>
          <div className="flex space-x-4">
            <input
              type="date"
              className="form-input"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            />
            <input
              type="date"
              className="form-input"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            />
          </div>
        </div>

        {/* Timesheets List */}
        {employeeTimesheets.length === 0 ? (
          <div className="card text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Timesheets Found</h3>
            <p className="text-gray-600">No timesheets found for the selected date range.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {employeeTimesheets.map((timesheet) => (
              <div key={timesheet.id} className="card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">
                      Week of {formatDate(timesheet.week_start_date)}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {timesheet.total_hours ? formatDuration(timesheet.total_hours * 60) : '0h 0m'} total
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(timesheet.status)}`}>
                      {timesheet.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    {timesheet.status === 'draft' && (
                      <button
                        onClick={() => handleSubmitTimesheet(timesheet.id)}
                        className="btn-primary text-sm"
                      >
                        Submit
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-lg font-semibold text-blue-900">
                      {timesheet.regular_hours ? formatDuration(timesheet.regular_hours * 60) : '0h'}
                    </div>
                    <div className="text-sm text-blue-600">Regular Hours</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-lg font-semibold text-yellow-900">
                      {timesheet.overtime_hours ? formatDuration(timesheet.overtime_hours * 60) : '0h'}
                    </div>
                    <div className="text-sm text-yellow-600">Overtime Hours</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-semibold text-green-900">
                      {timesheet.break_hours ? formatDuration(timesheet.break_hours * 60) : '0h'}
                    </div>
                    <div className="text-sm text-green-600">Break Time</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-semibold text-purple-900">
                      {timesheet.days_worked || 0}
                    </div>
                    <div className="text-sm text-purple-600">Days Worked</div>
                  </div>
                </div>

                {timesheet.compliance_notes && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500 mt-0.5 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-yellow-800">Compliance Notes</div>
                        <div className="text-sm text-yellow-700">{timesheet.compliance_notes}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderApprovalTab = () => {
    if (!isAdmin() && !isManager() && !isHRManager()) {
      return (
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to approve timesheets.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Pending Timesheet Approvals ({timesheets.length})
          </h3>
          <div className="flex space-x-4">
            <input
              type="date"
              className="form-input"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            />
            <input
              type="date"
              className="form-input"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            />
          </div>
        </div>

        {/* Timesheets List */}
        {timesheets.length === 0 ? (
          <div className="card text-center py-12">
            <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">No timesheets pending approval.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {timesheets.map((timesheet) => (
              <div key={timesheet.id} className="card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">
                      {timesheet.employee_name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      Week of {formatDate(timesheet.week_start_date)} • 
                      {timesheet.total_hours ? formatDuration(timesheet.total_hours * 60) : '0h 0m'} total
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApproveTimesheet(timesheet.id, false)}
                      className="btn-secondary bg-red-50 text-red-700 hover:bg-red-100 border-red-200"
                    >
                      <XCircleIcon className="w-4 h-4 mr-1" />
                      Reject
                    </button>
                    <button
                      onClick={() => handleApproveTimesheet(timesheet.id, true)}
                      className="btn-primary bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      Approve
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-lg font-semibold text-blue-900">
                      {timesheet.regular_hours ? formatDuration(timesheet.regular_hours * 60) : '0h'}
                    </div>
                    <div className="text-sm text-blue-600">Regular Hours</div>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-lg font-semibold text-yellow-900">
                      {timesheet.overtime_hours ? formatDuration(timesheet.overtime_hours * 60) : '0h'}
                    </div>
                    <div className="text-sm text-yellow-600">Overtime Hours</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-semibold text-green-900">
                      {timesheet.days_worked || 0}
                    </div>
                    <div className="text-sm text-green-600">Days Worked</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-semibold text-purple-900">
                      {timesheet.compliance_score || 100}%
                    </div>
                    <div className="text-sm text-purple-600">Compliance</div>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedTimesheet(timesheet)}
                  className="btn-secondary text-sm"
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderOvertimeTab = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Overtime Management</h3>
          <button
            onClick={() => setShowOvertimeModal(true)}
            className="btn-primary"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Request Overtime
          </button>
        </div>

        {/* Date Range */}
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="date"
              className="form-input"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            />
            <input
              type="date"
              className="form-input"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            />
            <button
              onClick={() => setDateRange({
                start_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
                end_date: new Date().toISOString().split('T')[0]
              })}
              className="btn-secondary"
            >
              Last 30 Days
            </button>
          </div>
        </div>

        {/* Overtime Records */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Planned Hours</th>
                  <th>Actual Hours</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {overtimeRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{record.employee_name}</td>
                    <td>{formatDate(record.date)}</td>
                    <td>{record.planned_hours}h</td>
                    <td>{record.actual_hours || '--'}h</td>
                    <td>{record.reason}</td>
                    <td>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record.status)}`}>
                        {record.status}
                      </span>
                    </td>
                    <td>
                      {record.status === 'pending' && (isAdmin() || isManager() || isHRManager()) && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => timeAttendanceAPI.approveOvertime(record.id, { approved: true })}
                            className="btn-secondary text-green-600 text-sm"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => timeAttendanceAPI.approveOvertime(record.id, { approved: false })}
                            className="btn-secondary text-red-600 text-sm"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderComplianceTab = () => {
    if (!isAdmin() && !isHRManager()) {
      return (
        <div className="text-center py-12">
          <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to view compliance reports.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Working Hours Compliance Report</h3>
          <div className="flex space-x-4">
            <input
              type="date"
              className="form-input"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            />
            <input
              type="date"
              className="form-input"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            />
          </div>
        </div>

        {complianceReport && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="card text-center">
                <div className="text-2xl font-bold text-blue-600 mb-2">
                  {complianceReport.total_employees || 0}
                </div>
                <div className="text-sm text-gray-600">Total Employees</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-green-600 mb-2">
                  {complianceReport.compliant_employees || 0}
                </div>
                <div className="text-sm text-gray-600">Compliant</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-red-600 mb-2">
                  {complianceReport.violations_count || 0}
                </div>
                <div className="text-sm text-gray-600">Violations</div>
              </div>
              <div className="card text-center">
                <div className="text-2xl font-bold text-purple-600 mb-2">
                  {complianceReport.compliance_score || 100}%
                </div>
                <div className="text-sm text-gray-600">Compliance Score</div>
              </div>
            </div>

            {/* Violations Breakdown */}
            {complianceReport.violation_types && (
              <div className="card">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Violations by Type</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {complianceReport.violation_types.map((violation, index) => (
                    <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold text-red-600 mb-2">
                        {violation.count}
                      </div>
                      <div className="text-sm text-gray-600">{violation.type}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'clock':
        return renderClockTab();
      case 'timesheets':
        return renderTimesheetsTab();
      case 'approval':
        return renderApprovalTab();
      case 'overtime':
        return renderOvertimeTab();
      case 'compliance':
        return renderComplianceTab();
      default:
        return null;
    }
  };

  // Filter tabs based on user role
  const visibleTabs = tabs.filter(tab => 
    tab.roles.includes('all') || 
    tab.roles.includes(user?.role) || 
    (tab.roles.includes('manager') && (isManager() || isAdmin() || isHRManager())) ||
    (tab.roles.includes('admin') && (isAdmin() || isHRManager())) ||
    (tab.roles.includes('hr_manager') && isHRManager())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Time & Attendance</h1>
          <p className="text-sm text-gray-600 mt-1">Track working hours with UK compliance monitoring</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`nav-item py-4 px-1 border-b-2 font-medium flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'nav-item-active border-blue-500 text-blue-600'
                      : 'nav-item-inactive border-transparent'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
      </div>

      {/* Overtime Request Modal */}
      {showOvertimeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Request Overtime Approval
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee
                  </label>
                  <select
                    className="form-input"
                    value={overtimeForm.employee_id}
                    onChange={(e) => setOvertimeForm({ ...overtimeForm, employee_id: e.target.value })}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    className="form-input"
                    value={overtimeForm.date}
                    onChange={(e) => setOvertimeForm({ ...overtimeForm, date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Planned Overtime Hours
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={overtimeForm.planned_hours}
                    onChange={(e) => setOvertimeForm({ ...overtimeForm, planned_hours: e.target.value })}
                    min="0.5"
                    max="12"
                    step="0.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Overtime
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={overtimeForm.reason}
                    onChange={(e) => setOvertimeForm({ ...overtimeForm, reason: e.target.value })}
                    placeholder="e.g., Project deadline, Staff shortage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={overtimeForm.notes}
                    onChange={(e) => setOvertimeForm({ ...overtimeForm, notes: e.target.value })}
                    placeholder="Additional details..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowOvertimeModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await timeAttendanceAPI.requestOvertimeApproval(overtimeForm);
                      setShowOvertimeModal(false);
                      setOvertimeForm({
                        employee_id: '',
                        date: new Date().toISOString().split('T')[0],
                        planned_hours: '',
                        reason: '',
                        notes: ''
                      });
                      fetchOvertimeRecords();
                    } catch (error) {
                      setError('Failed to submit overtime request');
                    }
                  }}
                  disabled={!overtimeForm.employee_id || !overtimeForm.date || !overtimeForm.planned_hours}
                  className="btn-primary"
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeAttendance;